/**
 * StreetSense map document (`streetsense.map/1`) — the authoring format.
 *
 * A map is DATA, not code: everything the sim needs to build a mission can be
 * written by a person in the editor, or by an AI from docs/MAP_FORMAT.md, and
 * checked by `validateMap` before it ever reaches the sim.
 *
 * Two authoring conveniences run through the whole format:
 *
 * 1. **Route-relative placement.** Anywhere a position is wanted you may give
 *    `{ s, d }` — s metres along the delivery route, d metres to the RIGHT of
 *    travel — instead of world `[x, z]`. "A dog sits 3 m past the 200 m mark,
 *    two metres to the left" is `{ s: 203, d: -2 }`, with no trigonometry.
 * 2. **Roads carry their traffic rules.** `oneWay` and `lanes` drive the ground
 *    markings the renderer paints AND the realism checks the validator makes,
 *    so a map cannot claim a one-way street and then send traffic up it —
 *    unless it says it means to (`role: "wrong_way"`).
 *
 * Pure data module: no DOM, no Babylon, no sim imports beyond plain types.
 */

/** World position, metres. */
export type XZ = [number, number];

/** Route-relative position: `s` along the route, `d` right of travel. */
export interface RouteRef {
  s: number;
  d: number;
}

/** Either form is accepted anywhere a position is wanted. */
export type Place = XZ | RouteRef;

export const MAP_SCHEMA = "streetsense.map/1";

// ---------------------------------------------------------------------------
// Roads

/** A straight-or-curved road: a centreline polyline with a width. */
export interface BandRoadDoc {
  id: string;
  kind: "alley" | "street";
  /** Centreline, world coords. Two points make a straight road. */
  pts: XZ[];
  /** Full carriageway width (m). Defaults: alley 3.6, street 9.0. */
  width?: number;
  /** One-way in the direction the points run. Default false (two-way). */
  oneWay?: boolean;
  /** Lanes per direction (markings only). Default 1. */
  lanes?: number;
  /**
   * False for pieces that only shape the drivable area (roundabout mouth
   * flares): they never define the steering frame. Default true.
   */
  frame?: boolean;
}

/** A roundabout: an annulus the traffic circulates around. */
export interface RingRoadDoc {
  id: string;
  kind: "roundabout";
  center: XZ;
  /** Island (inner curb) and outer curb radii, m. */
  innerR: number;
  outerR: number;
  /** Circulation sense seen from above. Default false = counter-clockwise
   * (right-hand traffic, which is what Vietnam drives). */
  clockwise?: boolean;
}

export type RoadDoc = BandRoadDoc | RingRoadDoc;

// ---------------------------------------------------------------------------
// Route

/** One waypoint of the delivery route. */
export interface RouteWaypointDoc {
  at: Place;
  /** Corridor half-width for off-route detection (m). Defaults to the road's. */
  halfWidth?: number;
  /** Corner fillet radius (m); 0 = sharp corner. */
  fillet?: number;
  /** Anchor name, so encounters and actors can say `["j1", -2]`. */
  name?: string;
  /** Road id this waypoint sits on (section bookkeeping). */
  road?: string;
}

/** A turn cue shown in the HUD, anchored to a named waypoint. */
export interface TurnDoc {
  at: string;
  kind: "left" | "right" | "roundabout" | "arrive";
  /** Roundabout only: which exit to take, 1-based. */
  exit?: number;
  label?: string;
}

/**
 * A step of a written route ("follow A1, turn right onto S1, 3rd exit of R").
 * The compiler resolves these into waypoints by intersecting road centrelines,
 * which is how a person or an AI describes a route without doing geometry.
 */
export type DirectionDoc =
  | { follow: string }
  | { turn: "left" | "right"; onto: string }
  | { roundabout: string; exit: number; onto: string }
  | { arrive: Place };

export interface RouteDoc {
  /** Explicit waypoints. Canonical: the editor always writes these. */
  waypoints?: RouteWaypointDoc[];
  /** Written directions, expanded into waypoints at compile time. */
  directions?: DirectionDoc[];
  turns?: TurnDoc[];
  /** Where the robot starts. Defaults to the first waypoint. */
  start?: { at: Place; heading?: number };
  /** The customer. Defaults to the last waypoint. */
  destination?: Place;
}

// ---------------------------------------------------------------------------
// Things in the world

/** A solid thing the robot can hit. */
export interface PropDoc {
  kind: string;
  at: Place;
  /** Footprint half-extents (m). Defaults come from the kind registry. */
  halfW?: number;
  halfD?: number;
  height?: number;
  /** Rotate the footprint 90° (a car parked along z instead of x). */
  across?: boolean;
  /**
   * A pit the driver cannot see coming (ADR-043): scripted players do not
   * steer around it, which is the rare case r01 exists to produce. A plain
   * pothole without this flag is visible and avoided like any obstacle.
   */
  unmarked?: boolean;
  /**
   * A role that deliberately breaks a rule, exactly as an actor's does
   * (registry.ts WEIRD_ROLES). `"blocking"` says this thing is MEANT to seal
   * the route: the validator then warns instead of refusing the map.
   *
   * It is per-prop rather than per-map on purpose. A map-level "allow blocked"
   * flag would silence the check for every other prop in the file, and the
   * check exists to catch the accident — a map where one obstacle was placed
   * two metres off and nobody noticed the route no longer fits.
   */
  role?: string;
}

/** Scenery. Tree trunks are solid; other kinds are visual only. */
export interface DecorDoc {
  kind: string;
  at: Place;
  /** Facing, radians from +z. */
  yaw?: number;
}

/** Everything that moves (or stands there being in the way). */
export interface ActorDoc {
  id: string;
  /**
   * Registry kind — `pedestrian`, `walker`, `stander`, `cat`, `dog`,
   * `motorbike`, `motorcycle`, `car`, `cart`, plus the weird ones
   * (`chicken`, `parade`, `broken_down_truck`, ...). See ACTOR_KINDS.
   */
  type: string;
  /** Free-text role for the renderer and the validator's realism rules. */
  role?: string;
  /** Where it starts / stands. */
  at?: Place;
  /** Where it walks or drives, in order. */
  path?: Place[];
  /** Crossing actors: the far side. */
  to?: Place;
  /** m/s. Defaults per kind. */
  speed?: number;
  /** Vehicles: travel backwards while the body faces forwards; defaults forward. */
  motion?: "forward" | "reverse";
  /** Route progress at which it comes alive (null/absent = immediately). */
  triggerAt?: number | null;
  /** Readable cue time before it first moves (s). */
  cueDelayS?: number;
  /** Facing for things that stand still, radians from +z. */
  facing?: number;
  /** Vehicles: stay at the end of the path, parked and solid. */
  parkAtEnd?: boolean;
  /** false: skip the automatic kerbside idle for a triggered vehicle
   * (hand-tuned choreography positions its wait itself). */
  kerbIdle?: boolean;
  /** Vehicles: may pull out and overtake a slower vehicle ahead. */
  canOvertake?: boolean;
  /** Vehicles: hold if the robot is this close behind and closing. */
  holdIfRobotBehindM?: number;
  /** Pets: which side of the route is home. */
  homeSide?: "left" | "right";
  /** Research pedestrian only: progress at which it starts walking. */
  walkAt?: number;
}

// ---------------------------------------------------------------------------
// Encounters

/** Zone bounds: `["anchor", offset]` or a plain route progress in metres. */
export type ZoneEdge = [string, number] | number;

/** A named stretch of the route, for telemetry bucketing. */
export interface SectionDoc {
  name: string;
  from: ZoneEdge;
  to: ZoneEdge;
  /** Road id this section is considered to be on. */
  road?: string;
}

export interface EncounterDoc {
  id: string;
  /** Free-text type, recorded per encounter in the telemetry. */
  type: string;
  from: ZoneEdge;
  to: ZoneEdge;
  /**
   * Road type recorded for this encounter. Defaults to whatever road the
   * middle of the zone sits on, which is right almost always; a junction
   * is the exception, since the manoeuvre belongs to the road being left
   * or joined rather than to the junction band itself.
   */
  roadType?: "alley" | "street" | "roundabout";
  /** Actor ids taking part. */
  actors?: string[];
  /**
   * Opt in to the controlled experiment. A research encounter is held to the
   * strict checks in validateMap (cue timing, no guidance inside the zone,
   * clean approach) and refuses to compile if it fails them.
   */
  research?: boolean;
  /** Section name shown in the telemetry. Defaults to the encounter id. */
  section?: string;
}

// ---------------------------------------------------------------------------

export interface ConditionsDoc {
  timeOfDay?: "day" | "dusk" | "night";
  weather?: "clear" | "rain" | "wet" | string;
  intensity?: string;
}

/**
 * A photograph of the real ground, laid where it was taken (ADR-098).
 *
 * The road surface is the one part of a real street whose geometry is not
 * estimated: the calibration puts the camera 0.649 m above a plane measured to
 * 5.8 mm, so every road pixel has a closed-form world position. `bev_route_map`
 * turns that into an orthographic image of the street; this says where to lay
 * it. The image is already exposed and tone-mapped by the camera, so the
 * renderer lights it flat rather than a second time.
 */
export interface ImageryDoc {
  /** Image URL, relative to the site root. */
  url: string;
  /** World position (x, z) of the image's top-left corner, in metres. */
  originXZ: [number, number];
  /** Metres per image pixel. */
  mpp: number;
  /** How far above the road to lay it; enough to beat z-fighting. */
  lift?: number;
  /**
   * This photograph IS the world: draw no sim road, kerb, pavement, building
   * or tree over it. Set on a map cut from the robot's own survey, where
   * every one of those would be invented geometry laid on top of measured
   * pixels — hiding the data underneath and passing for evidence above it.
   */
  only?: boolean;
}

/**
 * A Gaussian-splat reconstruction of this street (ADR-086/098).
 *
 * The splat carries the VOLUME — buildings, poles, parked scooters, trees —
 * that a ground photograph cannot. It is exported into the map's own frame by
 * `recon_align.py --recentre`, so it needs no placement here; `?splatpos=` is
 * still there for nudging one by hand.
 *
 * The two only line up if the reconstruction was aligned to the same track the
 * map's imagery came from. That means RTK: `recon_align.py --metric rtk`.
 */
export interface SplatDoc {
  /** URL of the .splat/.ply/.spz, relative to the site root. */
  url: string;
}

/**
 * The sky this street was photographed under (ADR-133).
 *
 * A reconstruction ships with its sky stripped out, because a gaussian placed
 * in the sky sits at a finite distance and parallaxes wrongly as soon as the
 * robot moves. Without this the street then arrives under the simulator's own
 * gradient — a different sky over a real place. `sky_dome.py` turns the sky
 * pixels of every photograph into one equirectangular panorama: at infinity
 * only the camera's rotation matters, so the direction of every sky pixel is
 * known and the average over the drive is the street's own sky.
 */
export interface SkyDoc {
  /** URL of an equirectangular image, relative to the site root. */
  url: string;
}

export interface MapDoc {
  schema: typeof MAP_SCHEMA;
  /** Stable id, recorded as `mission.map_id` in every episode. */
  id: string;
  name: string;
  description?: string;
  author?: string;
  /**
   * Whether this map may carry the controlled experiment at all. Absent or
   * false means EXPLORATION: every episode is hard-flagged non-research and
   * can never be pooled with study data. An encounter may only set
   * `research: true` if the map does too.
   */
  research?: boolean;
  /**
   * Ambience the map declares (ADR-054). The renderer honours timeOfDay
   * (sky, light, lamps, lit windows) and weather (wet sheen on the road);
   * the sim itself is unaffected.
   */
  conditions?: ConditionsDoc;
  roads: RoadDoc[];
  route: RouteDoc;
  /** A photograph of the real ground for this map, if there is one. */
  imagery?: ImageryDoc;
  /** A reconstruction of everything above that ground, if there is one. */
  splat?: SplatDoc;
  /** The sky it was photographed under, if it was captured. */
  sky?: SkyDoc;
  props?: PropDoc[];
  decor?: DecorDoc[];
  actors?: ActorDoc[];
  /**
   * Named stretches of the route for the telemetry. Optional: without
   * them each encounter becomes its own section, which is what a simple
   * map wants. Several encounters may share one section.
   */
  sections?: SectionDoc[];
  encounters?: EncounterDoc[];
  /** Attempt timeout (s). Defaults to max(150, 1.5 x route / cruise). */
  timeoutS?: number;
  /** Catalogue id of the situation this map stages (e.g. "SS-VEH-01"). */
  case_id?: string;
}

// ---------------------------------------------------------------------------
// Validation issues

export interface MapIssue {
  /** Stable machine-readable rule identifier (not episode telemetry). */
  code?: string;
  level: "error" | "warning";
  /** Where in the document, e.g. `actors[3].path[1]`. */
  path: string;
  message: string;
  /** World position to point a camera at, when the issue has one. */
  at?: XZ;
  /** What to do about it, in plain language. */
  fix?: string;
}

export function isRing(r: RoadDoc): r is RingRoadDoc {
  return r.kind === "roundabout";
}

export function isPlaceRouteRef(p: Place): p is RouteRef {
  return !Array.isArray(p);
}
